import { RefObject, useEffect, useRef } from 'react'
import {
  ItemContentRect,
  computeHitIds,
  edgeScrollSpeed,
  resolveScrollContainer,
  snapshotItemContentRects
} from './boxSelectGeometry'

/** useBoxSelect 配置参数 */
export interface UseBoxSelectOptions {
  /** 框选作用的容器 · mousedown 绑定与命中检测都在此范围内 */
  containerRef: RefObject<HTMLElement>
  /** 可选项的标识属性名 · 如 'data-folder-id'：起点过滤排除它、命中检测读它的值作为 id */
  itemAttr: string
  /**
   * 命中回调 · 拖动过程中**实时**上抛当前框中的 id 列表（含空集，便于实时取消高亮）。
   * 已按帧节流且仅命中集合变化时触发；松手时再精确上抛一次最终结果。
   */
  onSelect: (ids: string[]) => void
  /** 框选进入 active（越过阈值开始画框）时触发一次 · 用于进入多选 / 标记"框选进行中" */
  onSelectStart?: () => void
  /** 框选会话结束（松手 / Esc / 窗口失焦）时触发一次 · 仅在曾进入 active 后触发 · 用于收尾多选生命周期 */
  onSelectEnd?: () => void
  /** 起点在空白且未拖动（普通点击）时触发 · 调用方可用它做"点空白退出多选"等 */
  onEmptyClick?: () => void
  /** 为 true 时不启用框选 */
  disabled?: boolean
  /** 显式滚动容器 · 提供时用它替代向上查找最近可滚动祖先（react-virtualized Grid 自带滚动节点） */
  scrollElementRef?: RefObject<HTMLElement>
  /** 显式提供全量可选项内容坐标矩形 · 提供时用它替代 DOM 快照（虚拟滚动下覆盖视口外项） */
  getItemRects?: () => ItemContentRect[]
}

/** 进入 active（开始画框、松手才算真正框选）的最小拖动位移，单位 px */
const ACTIVE_THRESHOLD = 5

/** 显式排除区的属性名 · 起点落在带此属性的元素内则不进入框选（表头 / 操作条 / 空态等） */
const EXCLUDE_ATTR = 'data-no-box-select'

/** 触发边缘自动滚动的边缘区宽度（距视口上 / 下边，单位 px） */
const EDGE_SCROLL_ZONE = 48

/** 边缘自动滚动满速（px / 帧，约 60 帧/秒） */
const MAX_EDGE_SCROLL_SPEED = 18

/** 实时命中上抛的最小间隔（ms）· 画框 / 滚动每帧都做，但 dispatch 限频，省 React 重渲染 */
const SELECT_EMIT_INTERVAL = 50

/** 框选会话数据 · 全程用内容坐标，对滚动免疫；用 ref 持有不进 state，避免高频重渲染 */
interface BoxSelectSession {
  /** 起点客户端坐标 · 仅用于 ACTIVE_THRESHOLD 阈值判断 */
  startClientX: number
  startClientY: number
  /** 起点内容坐标 · 选区固定的一角，滚动中不变 */
  anchorContentX: number
  anchorContentY: number
  /** 是否已越过阈值真正开始画框 */
  active: boolean
  /** 框选作用容器 · 命中检测在其内 querySelectorAll（存进会话避免闭包内 null 收窄丢失） */
  container: HTMLElement
  /** 滚动容器（视口 + 自动滚动对象） */
  scroller: HTMLElement
  /** 滚动容器客户端 rect · 拖动期间视口不动，缓存一次 */
  viewportRect: DOMRect
  /** 最大可滚动距离（scrollHeight - clientHeight）· active 时算一次，用于 clamp */
  maxScrollTop: number
  /** 各可选项内容坐标快照 · active 时算一次，逐帧命中只读它 */
  itemRects: ItemContentRect[]
  /** 最新指针客户端坐标 · 指针不动但停在边缘区时，自动滚动仍靠它持续推进 */
  lastClientX: number
  lastClientY: number
  /** 上次已上抛的命中签名 + 时刻 · 配合节流，集合不变 / 未到间隔就不重复 dispatch */
  lastEmittedKey: string
  lastEmittedAt: number
  /** 当前 requestAnimationFrame 句柄 · 结束时 cancel */
  rafId: number
}

/**
 * 鼠标框选 hook · 在 containerRef 容器内拉矩形批量选中，支持拖到边缘自动滚动扩选。
 *
 * 由列表类组件通过 <BoxSelect> 间接调用（如 FolderList）：传入容器 ref 与可选项属性名，
 * 命中结果通过 onSelect 实时上抛。
 *
 * 关键设计：
 *  · 内容坐标系 —— 选区锚点与各项位置都换算成"相对滚动内容"的坐标，拖动中容器
 *    自动滚动也无需重算，命中检测只做算术、不逐帧读布局（各项位置 active 时快照一次）
 *  · 单 rAF 主循环 —— active 后每帧统一做"边缘滚动 + 画框 + 命中"，天然每帧一次；
 *    画框命令式改 div style（不重渲染），命中上抛按帧节流 + 集合变化才 dispatch
 *  · 零业务知识 —— 只算"命中的 id 列表"，怎么应用由调用方在 onSelect / onSelectStart /
 *    onSelectEnd 里自管
 *  · 全局监听在 mousedown 时命令式绑定、会话结束解绑，不依赖会驱动渲染的 state
 *
 * @param options - 配置参数，各字段含义见 UseBoxSelectOptions
 * @returns 框选可视化矩形 div 的 ref · 由 <BoxSelect> 挂到矩形节点上
 */
export function useBoxSelect({
  containerRef,
  itemAttr,
  onSelect,
  onSelectStart,
  onSelectEnd,
  onEmptyClick,
  disabled,
  scrollElementRef,
  getItemRects
}: UseBoxSelectOptions): RefObject<HTMLDivElement> {
  const rectRef = useRef<HTMLDivElement>(null)
  const sessionRef = useRef<BoxSelectSession | null>(null)
  /** 框选期间被改写的 body.userSelect 原值 · 会话结束时还原 */
  const prevBodyUserSelectRef = useRef<string>('')
  /** 当前活跃会话的清理函数 · 供卸载 / disabled 切换时兜底解绑 */
  const cleanupRef = useRef<(() => void) | null>(null)

  // latestRef 模式存最新回调 / itemAttr · 让绑定 mousedown 的 effect 只依赖
  // containerRef / disabled，回调或属性名变化时不重绑监听
  const onSelectRef = useRef(onSelect)
  const onSelectStartRef = useRef(onSelectStart)
  const onSelectEndRef = useRef(onSelectEnd)
  const onEmptyClickRef = useRef(onEmptyClick)
  const itemAttrRef = useRef(itemAttr)
  // getItemRects 也走 latestRef · folders 变化导致它换引用时，只更新 ref，
  // 不重绑 mousedown 监听（绑定 effect 仍只依赖 containerRef / disabled）
  const getItemRectsRef = useRef(getItemRects)
  useEffect(() => {
    onSelectRef.current = onSelect
    onSelectStartRef.current = onSelectStart
    onSelectEndRef.current = onSelectEnd
    onEmptyClickRef.current = onEmptyClick
    itemAttrRef.current = itemAttr
    getItemRectsRef.current = getItemRects
  }, [
    onSelect,
    onSelectStart,
    onSelectEnd,
    onEmptyClick,
    itemAttr,
    getItemRects
  ])

  useEffect(() => {
    const container = containerRef.current
    if (!container || disabled) return

    /** 命中上抛 · 算出当前选区命中的 id，节流后通过 onSelect 上抛（force 时绕过节流） */
    const emitHits = (
      session: BoxSelectSession,
      left: number,
      right: number,
      top: number,
      bottom: number,
      force: boolean
    ): void => {
      const hits = computeHitIds(session.itemRects, left, right, top, bottom)
      const key = hits.join(',')
      const now = performance.now()
      if (!force) {
        if (key === session.lastEmittedKey) return
        if (now - session.lastEmittedAt < SELECT_EMIT_INTERVAL) return
      }
      session.lastEmittedKey = key
      session.lastEmittedAt = now
      onSelectRef.current(hits)
    }

    const onDown = (e: MouseEvent): void => {
      if (e.button !== 0) return // 仅左键
      const target = e.target as HTMLElement
      // 起点在可选项上 → 让卡片自己的 onClick / Dropdown / 拖拽接管
      if (target.closest(`[${itemAttrRef.current}]`)) return
      // 起点在显式排除区（表头 / 操作条 / 空态等）→ 跳过
      if (target.closest(`[${EXCLUDE_ATTR}]`)) return
      // 阻止默认 · 否则 mousedown 会启动浏览器文本选择，与拖框冲突
      e.preventDefault()

      // 优先用显式滚动容器（虚拟滚动 Grid 自带滚动节点）；未提供时回退向上查找
      const scroller =
        scrollElementRef?.current || resolveScrollContainer(container)
      const viewportRect = scroller.getBoundingClientRect()
      sessionRef.current = {
        startClientX: e.clientX,
        startClientY: e.clientY,
        anchorContentX: e.clientX - viewportRect.left + scroller.scrollLeft,
        anchorContentY: e.clientY - viewportRect.top + scroller.scrollTop,
        active: false,
        container,
        scroller,
        viewportRect,
        maxScrollTop: 0,
        itemRects: [],
        lastClientX: e.clientX,
        lastClientY: e.clientY,
        lastEmittedKey: '',
        lastEmittedAt: 0,
        rafId: 0
      }
      // 整个会话期间禁掉 body 的 user-select，已选中的文字也清掉，
      // 避免框选过程中视觉上有蓝色文字选区干扰
      prevBodyUserSelectRef.current = document.body.style.userSelect
      document.body.style.userSelect = 'none'
      const selection = window.getSelection()
      if (selection) selection.removeAllRanges()

      // 会话结束统一清理 · 停 rAF + 解绑监听 + 隐藏矩形 + 还原 user-select + 收尾回调
      // 用函数声明（提升）让下方各 handler 能互相引用，顺序无关
      function cleanup(): void {
        const session = sessionRef.current
        if (session && session.rafId) cancelAnimationFrame(session.rafId)
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        window.removeEventListener('keydown', onKey)
        window.removeEventListener('blur', onWindowBlur)
        const wasActive = !!session?.active
        sessionRef.current = null
        cleanupRef.current = null
        const rectEl = rectRef.current
        if (rectEl) rectEl.style.display = 'none'
        document.body.style.userSelect = prevBodyUserSelectRef.current
        if (wasActive) {
          const notifyEnd = onSelectEndRef.current
          if (notifyEnd) notifyEnd()
        }
      }

      // 越过阈值 → 正式开始画框：快照各项位置、通知调用方、启动 rAF 主循环
      function activate(session: BoxSelectSession): void {
        session.active = true
        session.maxScrollTop =
          session.scroller.scrollHeight - session.scroller.clientHeight
        // 优先用显式几何模型（覆盖视口外未渲染的项）；未提供时回退 DOM 快照
        session.itemRects = getItemRectsRef.current
          ? getItemRectsRef.current()
          : snapshotItemContentRects(
              session.container,
              itemAttrRef.current,
              session.scroller,
              session.viewportRect
            )
        const notifyStart = onSelectStartRef.current
        if (notifyStart) notifyStart()
        runFrame()
      }

      // rAF 主循环 · active 期间每帧统一处理"边缘滚动 + 画框 + 命中"
      function runFrame(): void {
        const session = sessionRef.current
        if (!session || !session.active) return
        const { scroller, viewportRect } = session

        // 1. 边缘自动滚动 · 指针落在视口上 / 下边缘区时按深度滚动
        const distTop = session.lastClientY - viewportRect.top
        const distBottom = viewportRect.bottom - session.lastClientY
        let scrollDelta = 0
        if (distTop < EDGE_SCROLL_ZONE) {
          scrollDelta = -edgeScrollSpeed(
            EDGE_SCROLL_ZONE - distTop,
            EDGE_SCROLL_ZONE,
            MAX_EDGE_SCROLL_SPEED
          )
        } else if (distBottom < EDGE_SCROLL_ZONE) {
          scrollDelta = edgeScrollSpeed(
            EDGE_SCROLL_ZONE - distBottom,
            EDGE_SCROLL_ZONE,
            MAX_EDGE_SCROLL_SPEED
          )
        }
        if (scrollDelta !== 0) {
          scroller.scrollTop = Math.max(
            0,
            Math.min(scroller.scrollTop + scrollDelta, session.maxScrollTop)
          )
        }

        // 2. 当前滚动位置 → 指针内容坐标
        // 指针 clamp 到视口内：贴边自动滚动时，选区会随 scrollTop 增长延伸到新滚入的内容
        const { scrollTop, scrollLeft } = scroller
        const pointerX =
          Math.max(
            viewportRect.left,
            Math.min(session.lastClientX, viewportRect.right)
          ) -
          viewportRect.left +
          scrollLeft
        const pointerY =
          Math.max(
            viewportRect.top,
            Math.min(session.lastClientY, viewportRect.bottom)
          ) -
          viewportRect.top +
          scrollTop
        const left = Math.min(session.anchorContentX, pointerX)
        const right = Math.max(session.anchorContentX, pointerX)
        const top = Math.min(session.anchorContentY, pointerY)
        const bottom = Math.max(session.anchorContentY, pointerY)

        // 3. 画矩形 · 内容坐标 → 客户端坐标，裁剪到视口（锚点滚出屏幕时贴边）
        const rectEl = rectRef.current
        if (rectEl) {
          const drawLeft = Math.max(
            left - scrollLeft + viewportRect.left,
            viewportRect.left
          )
          const drawRight = Math.min(
            right - scrollLeft + viewportRect.left,
            viewportRect.right
          )
          const drawTop = Math.max(
            top - scrollTop + viewportRect.top,
            viewportRect.top
          )
          const drawBottom = Math.min(
            bottom - scrollTop + viewportRect.top,
            viewportRect.bottom
          )
          if (drawRight <= drawLeft || drawBottom <= drawTop) {
            rectEl.style.display = 'none'
          } else {
            rectEl.style.display = 'block'
            rectEl.style.left = `${drawLeft}px`
            rectEl.style.top = `${drawTop}px`
            rectEl.style.width = `${drawRight - drawLeft}px`
            rectEl.style.height = `${drawBottom - drawTop}px`
          }
        }

        // 4. 命中 · 按帧节流 + 集合变化才上抛
        emitHits(session, left, right, top, bottom, false)

        session.rafId = requestAnimationFrame(runFrame)
      }

      // 拖动 · 只记录最新指针位置 + 判断是否进入 active；重活都在 runFrame 里
      function onMove(ev: MouseEvent): void {
        ev.preventDefault() // 双保险 · 防止拖动时选中页面文字
        const session = sessionRef.current
        if (!session) return
        // 已松开却仍收到 move（在窗口外松手后又移回）→ 收尾，防止自动滚动卡死
        if (ev.buttons === 0) {
          cleanup()
          return
        }
        session.lastClientX = ev.clientX
        session.lastClientY = ev.clientY
        if (
          !session.active &&
          Math.hypot(
            ev.clientX - session.startClientX,
            ev.clientY - session.startClientY
          ) > ACTIVE_THRESHOLD
        ) {
          activate(session)
        }
      }

      // 松手 · active 用最后位置精确上抛一次（绕过节流）；未拖动（普通点击）触发 onEmptyClick
      function onUp(ev: MouseEvent): void {
        const session = sessionRef.current
        if (session && session.active) {
          session.lastClientX = ev.clientX
          session.lastClientY = ev.clientY
          const { scrollTop, scrollLeft } = session.scroller
          const pointerX =
            Math.max(
              session.viewportRect.left,
              Math.min(ev.clientX, session.viewportRect.right)
            ) -
            session.viewportRect.left +
            scrollLeft
          const pointerY =
            Math.max(
              session.viewportRect.top,
              Math.min(ev.clientY, session.viewportRect.bottom)
            ) -
            session.viewportRect.top +
            scrollTop
          emitHits(
            session,
            Math.min(session.anchorContentX, pointerX),
            Math.max(session.anchorContentX, pointerX),
            Math.min(session.anchorContentY, pointerY),
            Math.max(session.anchorContentY, pointerY),
            true
          )
        } else if (session) {
          const notifyEmptyClick = onEmptyClickRef.current
          if (notifyEmptyClick) notifyEmptyClick()
        }
        cleanup()
      }

      function onKey(ev: KeyboardEvent): void {
        if (ev.key === 'Escape') cleanup()
      }

      // 窗口失焦（如 alt-tab）· 兜底收尾，避免会话 / 自动滚动悬挂
      function onWindowBlur(): void {
        cleanup()
      }

      cleanupRef.current = cleanup
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
      window.addEventListener('keydown', onKey)
      window.addEventListener('blur', onWindowBlur)
    }

    container.addEventListener('mousedown', onDown)
    return () => {
      container.removeEventListener('mousedown', onDown)
      // 卸载 / disabled 切换时若仍有进行中的会话 → 兜底清理
      if (cleanupRef.current) cleanupRef.current()
    }
  }, [containerRef, disabled])

  return rectRef
}
